import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import type { Address } from 'viem'
import { INTERFACE_PRESETS } from '../constants'
import { abiToFunctionSignatures, fetchVerifiedContractAbi } from '../lib/contractAbi'
import {
  allowedCallSignatureKey,
  computeSelector,
  getEffectiveFunctionSignatures,
  type AllowedCallEntry,
} from '../utils'

interface AllowedCallsEditorProps {
  entries: AllowedCallEntry[]
  onChange: (entries: AllowedCallEntry[]) => void
  /** When true, show SUPER_CALL active alert inside this section */
  hasSuperCall?: boolean
  /** Chain used for explorer ABI lookup (LUKSO mainnet 42 / testnet 4201). */
  chainId?: number
}

function createEmptyEntry(): AllowedCallEntry {
  return {
    id: crypto.randomUUID(),
    callTypes: { call: true, staticCall: false, delegateCall: false, transferValue: false },
    address: '',
    useAnyAddress: true,
    interfaceId: '',
    useAnyInterface: true,
    functionInput: '',
    functionSignatures: [],
    useAnyFunction: true,
  }
}

export function AllowedCallsEditor({
  entries,
  onChange,
  hasSuperCall,
  chainId = 42,
}: AllowedCallsEditorProps) {
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null)

  const addEntry = () => {
    const newEntry = createEmptyEntry()
    onChange([...entries, newEntry])
    setExpandedEntry(newEntry.id)
  }

  const removeEntry = (id: string) => {
    onChange(entries.filter(e => e.id !== id))
  }

  const updateEntry = (id: string, updates: Partial<AllowedCallEntry>) => {
    onChange(entries.map(e => e.id === id ? { ...e, ...updates } : e))
  }

  return (
    <div className="card-hub">
      <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
        <svg className="w-5 h-5 text-lukso-purple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Allowed Calls
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Restrict which contracts, functions, or standards this controller can interact with. If SUPER_CALL is selected, this controller can call any contract without restrictions, regardless of Allowed Calls.
      </p>

      {hasSuperCall && (
        <div className="p-4 bg-purple-500/10 rounded-xl border border-purple-500/30 mb-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <div>
              <h4 className="font-medium text-purple-300">SUPER_CALL Active</h4>
              <p className="text-sm text-purple-300/90 mt-1">
                Allowed Calls restrictions are bypassed — this controller can call any contract without restrictions.
              </p>
            </div>
          </div>
        </div>
      )}

      {entries.length > 0 && (
        <div className="space-y-3 mb-4">
          {entries.map((entry, index) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              index={index}
              chainId={chainId}
              isExpanded={expandedEntry === entry.id}
              onToggle={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
              onUpdate={(updates) => updateEntry(entry.id, updates)}
              onRemove={() => removeEntry(entry.id)}
            />
          ))}
        </div>
      )}

      <button
        onClick={addEntry}
        className="w-full py-2.5 px-4 rounded-lg border border-dashed border-white/[0.12] bg-white/[0.04] text-gray-400 hover:border-white/20 hover:text-gray-300 transition-all text-sm font-medium flex items-center justify-center gap-2"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add Allowed Call Entry
      </button>
    </div>
  )
}

interface EntryCardProps {
  entry: AllowedCallEntry
  index: number
  chainId: number
  isExpanded: boolean
  onToggle: () => void
  onUpdate: (updates: Partial<AllowedCallEntry>) => void
  onRemove: () => void
}

function EntryCard({ entry, index, chainId, isExpanded, onToggle, onUpdate, onRemove }: EntryCardProps) {
  const [abiSignatures, setAbiSignatures] = useState<string[] | null>(null)
  const [abiStatus, setAbiStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [abiHint, setAbiHint] = useState<string | null>(null)

  const addressValid = useMemo(
    () => /^0x[a-fA-F0-9]{40}$/.test(entry.address.trim()),
    [entry.address],
  )

  const addressRef = useRef(entry.address)
  addressRef.current = entry.address

  const loadAbi = useCallback(async () => {
    if (entry.useAnyAddress || !addressValid) {
      setAbiSignatures(null)
      setAbiStatus('idle')
      setAbiHint(null)
      return
    }
    const addr = entry.address.trim()
    const snapshot = addr.toLowerCase()
    setAbiStatus('loading')
    setAbiHint(null)
    const res = await fetchVerifiedContractAbi(chainId, addr as Address)
    if (addressRef.current.trim().toLowerCase() !== snapshot) return
    if ('error' in res) {
      setAbiStatus('error')
      setAbiSignatures(null)
      setAbiHint(res.error)
      return
    }
    const sigs = abiToFunctionSignatures(res.abi)
    setAbiStatus('success')
    setAbiSignatures(sigs.length > 0 ? sigs : null)
    setAbiHint(sigs.length === 0 ? 'Verified contract has no public functions in ABI.' : null)
  }, [entry.useAnyAddress, entry.address, addressValid, chainId])

  useEffect(() => {
    if (entry.useAnyFunction || entry.useAnyAddress) {
      setAbiSignatures(null)
      setAbiStatus('idle')
      setAbiHint(null)
      return
    }
    if (!addressValid) {
      setAbiSignatures(null)
      setAbiStatus('idle')
      setAbiHint(null)
      return
    }
    const t = window.setTimeout(() => {
      void loadAbi()
    }, 500)
    return () => {
      window.clearTimeout(t)
    }
  }, [entry.useAnyFunction, entry.useAnyAddress, entry.address, addressValid, chainId, loadAbi])

  const [fnFilter, setFnFilter] = useState('')
  const [manualAdd, setManualAdd] = useState('')

  const effectiveSigs = useMemo(() => getEffectiveFunctionSignatures(entry), [entry])

  const filteredAbiSigs = useMemo(() => {
    if (!abiSignatures?.length) return []
    const q = fnFilter.trim().toLowerCase()
    if (!q) return abiSignatures
    return abiSignatures.filter(s => s.toLowerCase().includes(q))
  }, [abiSignatures, fnFilter])

  const summary = useMemo(() => {
    const parts: string[] = []
    const types: string[] = []
    if (entry.callTypes.call) types.push('CALL')
    if (entry.callTypes.staticCall) types.push('STATICCALL')
    if (entry.callTypes.delegateCall) types.push('DELEGATECALL')
    if (entry.callTypes.transferValue) types.push('VALUE')
    parts.push(types.join('+') || 'No call type')

    if (entry.useAnyAddress) {
      parts.push('any address')
    } else if (entry.address) {
      parts.push(`${entry.address.slice(0, 8)}...`)
    }

    if (!entry.useAnyInterface && entry.interfaceId) {
      const preset = Object.values(INTERFACE_PRESETS).find(p => p.id === entry.interfaceId)
      parts.push(preset ? preset.name : entry.interfaceId)
    }

    if (!entry.useAnyFunction) {
      const eff = getEffectiveFunctionSignatures(entry)
      if (eff.length > 1) parts.push(`${eff.length} functions`)
      else if (eff.length === 1) parts.push(computeSelector(eff[0]) || eff[0])
    }

    return parts.join(' → ')
  }, [entry])

  const toggleSignature = (sig: string) => {
    const k = allowedCallSignatureKey(sig)
    if (!k) return
    let base = [...entry.functionSignatures]
    if (base.length === 0 && entry.functionInput.trim()) base = [entry.functionInput.trim()]
    const has = base.some(s => allowedCallSignatureKey(s) === k)
    const next = has ? base.filter(s => allowedCallSignatureKey(s) !== k) : [...base, sig]
    onUpdate({ functionSignatures: next, functionInput: '' })
  }

  const addSignature = (raw: string) => {
    const t = raw.trim()
    if (!computeSelector(t)) return
    const k = allowedCallSignatureKey(t)
    if (!k) return
    let base = [...entry.functionSignatures]
    if (base.length === 0 && entry.functionInput.trim()) base = [entry.functionInput.trim()]
    if (base.some(s => allowedCallSignatureKey(s) === k)) return
    onUpdate({ functionSignatures: [...base, t], functionInput: '' })
  }

  const selectAllFiltered = () => {
    let base = [...entry.functionSignatures]
    if (base.length === 0 && entry.functionInput.trim()) base = [entry.functionInput.trim()]
    const seen = new Set(base.map(s => allowedCallSignatureKey(s)).filter(Boolean))
    for (const s of filteredAbiSigs) {
      const k = allowedCallSignatureKey(s)
      if (k && !seen.has(k)) {
        seen.add(k)
        base.push(s)
      }
    }
    onUpdate({ functionSignatures: base, functionInput: '' })
  }

  const clearAllFunctions = () => {
    onUpdate({ functionSignatures: [], functionInput: '' })
  }

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/[0.06] transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-xs font-bold text-gray-500">#{index + 1}</span>
          <span className="text-sm text-gray-300 truncate">{summary}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onRemove() }}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
            title="Remove entry"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Body */}
      {isExpanded && (
        <div className="px-4 py-4 space-y-4 border-t border-white/[0.06]">
          {/* Call Types */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Allowed call types</label>
            <div className="flex flex-wrap gap-3">
              {[
                { key: 'call' as const, label: 'CALL', desc: 'Contract calls (e.g. token transfer)' },
                { key: 'staticCall' as const, label: 'STATICCALL', desc: 'Read-only queries' },
                { key: 'transferValue' as const, label: 'VALUE', desc: 'Attach native LYX (not ERC20/LSP7 transfer)' },
                { key: 'delegateCall' as const, label: 'DELEGATECALL', desc: '⚠️ Dangerous' },
              ].map(({ key, label, desc }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={entry.callTypes[key]}
                    onChange={(e) => onUpdate({ callTypes: { ...entry.callTypes, [key]: e.target.checked } })}
                    className="rounded border-gray-300 text-lukso-pink focus:ring-lukso-pink"
                  />
                  <span className="text-sm">
                    {label}
                    <span className="text-xs text-gray-400 ml-1">({desc})</span>
                  </span>
                </label>
              ))}
            </div>
            {entry.callTypes.delegateCall && (
              <p className="mt-1 text-xs text-red-500">⚠️ Delegate call allows executing code in the profile's context — extremely dangerous!</p>
            )}
            {!entry.callTypes.call && !entry.callTypes.staticCall && !entry.callTypes.delegateCall && !entry.callTypes.transferValue && (
              <p className="mt-1 text-xs text-red-500">At least one call type must be selected</p>
            )}
          </div>

          {/* Target Address */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-300">Target Address</label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={entry.useAnyAddress}
                  onChange={(e) => onUpdate({ useAnyAddress: e.target.checked })}
                  className="rounded border-gray-300 text-lukso-pink focus:ring-lukso-pink"
                />
                <span className="text-xs text-gray-500">Any address</span>
              </label>
            </div>
            {!entry.useAnyAddress && (
              <input
                type="text"
                value={entry.address}
                onChange={(e) => onUpdate({ address: e.target.value })}
                placeholder="0x..."
                className="input input-subtle w-full px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-lukso-pink focus:border-transparent"
              />
            )}
            {!entry.useAnyAddress && entry.address && !/^0x[a-fA-F0-9]{40}$/.test(entry.address) && (
              <p className="mt-1 text-xs text-red-500">Invalid address format</p>
            )}
          </div>

          {/* Interface Standard */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-300">Interface Standard</label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={entry.useAnyInterface}
                  onChange={(e) => onUpdate({ useAnyInterface: e.target.checked })}
                  className="rounded border-gray-300 text-lukso-pink focus:ring-lukso-pink"
                />
                <span className="text-xs text-gray-500">Any interface</span>
              </label>
            </div>
            {!entry.useAnyInterface && (
              <>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {Object.entries(INTERFACE_PRESETS).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => onUpdate({ interfaceId: preset.id })}
                      className={`text-xs px-2.5 py-1 rounded-lg border transition-all ${
                        entry.interfaceId === preset.id
                          ? 'border-lukso-pink bg-lukso-pink/10 text-lukso-pink'
                          : 'border-white/10 text-gray-400 hover:border-white/20 hover:text-gray-300'
                      }`}
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={entry.interfaceId}
                  onChange={(e) => onUpdate({ interfaceId: e.target.value })}
                  placeholder="0xNNNNNNNN (4 bytes)"
                  className="input input-subtle w-full px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-lukso-pink focus:border-transparent"
                />
              </>
            )}
          </div>

          {/* Function selectors — LSP6: one on-chain element per function; multi-select expands to several */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-300">Functions</label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={entry.useAnyFunction}
                  onChange={(e) =>
                    onUpdate({
                      useAnyFunction: e.target.checked,
                      functionSignatures: [],
                      functionInput: '',
                    })}
                  className="rounded border-gray-300 text-lukso-pink focus:ring-lukso-pink"
                />
                <span className="text-xs text-gray-500">Any function</span>
              </label>
            </div>
            {!entry.useAnyFunction && (
              <>
                {!entry.useAnyAddress && addressValid && (
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    {abiStatus === 'loading' && (
                      <span className="text-xs text-gray-500 inline-flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Loading verified ABI from explorer…
                      </span>
                    )}
                    {abiStatus === 'success' && abiSignatures && abiSignatures.length > 0 && (
                      <span className="text-xs text-green-600 dark:text-green-400">
                        {abiSignatures.length} function(s) in verified ABI — tick any combination.
                      </span>
                    )}
                    {abiStatus === 'error' && abiHint && (
                      <span className="text-xs text-amber-600 dark:text-amber-400" title={abiHint}>
                        Could not auto-load ABI. {abiHint.slice(0, 90)}
                        {abiHint.length > 90 ? '…' : ''}
                      </span>
                    )}
                    {abiHint && abiStatus === 'success' && (
                      <span className="text-xs text-gray-500">{abiHint}</span>
                    )}
                    <button
                      type="button"
                      onClick={() => void loadAbi()}
                      disabled={!addressValid || abiStatus === 'loading'}
                      className="text-xs px-2 py-1 rounded-md border border-white/15 text-gray-400 hover:text-gray-200 hover:border-white/25 disabled:opacity-50"
                    >
                      Refresh ABI
                    </button>
                  </div>
                )}

                {abiSignatures && abiSignatures.length > 0 && (
                  <div className="mb-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="search"
                        value={fnFilter}
                        onChange={(e) => setFnFilter(e.target.value)}
                        placeholder="Filter functions…"
                        className="input input-subtle flex-1 min-w-[140px] px-3 py-1.5 text-xs font-mono"
                      />
                      <button
                        type="button"
                        onClick={selectAllFiltered}
                        className="text-xs px-2 py-1 rounded-md border border-white/15 text-gray-400 hover:text-gray-200"
                      >
                        Select all (filtered)
                      </button>
                      <button
                        type="button"
                        onClick={clearAllFunctions}
                        className="text-xs px-2 py-1 rounded-md border border-white/15 text-gray-400 hover:text-gray-200"
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="max-h-48 overflow-y-auto rounded-lg border border-white/[0.08] bg-white/[0.03] p-2 space-y-1">
                      {filteredAbiSigs.length === 0 ? (
                        <p className="text-xs text-gray-500 px-1">No functions match filter.</p>
                      ) : (
                        filteredAbiSigs.map(sig => {
                          const checked = effectiveSigs.some(s => allowedCallSignatureKey(s) === allowedCallSignatureKey(sig))
                          return (
                            <label
                              key={sig}
                              className="flex items-start gap-2 cursor-pointer rounded-md px-2 py-1.5 hover:bg-white/[0.06] text-xs font-mono text-gray-300"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSignature(sig)}
                                className="mt-0.5 rounded border-gray-500 text-lukso-pink focus:ring-lukso-pink shrink-0"
                              />
                              <span className="break-all">{sig}</span>
                            </label>
                          )
                        })
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Add manually (selector or full signature)</label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={manualAdd}
                      onChange={(e) => setManualAdd(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          addSignature(manualAdd)
                          setManualAdd('')
                        }
                      }}
                      placeholder="0xaabbccdd or transfer(address,uint256)"
                      className="input input-subtle flex-1 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-lukso-pink focus:border-transparent"
                    />
                    <button
                      type="button"
                      className="btn-secondary text-sm shrink-0"
                      onClick={() => {
                        addSignature(manualAdd)
                        setManualAdd('')
                      }}
                    >
                      Add
                    </button>
                  </div>
                </div>

                {effectiveSigs.length > 0 && (
                  <p className="mt-2 text-xs text-gray-400">
                    Selected: <span className="text-gray-300 font-medium">{effectiveSigs.length}</span> function
                    {effectiveSigs.length === 1 ? '' : 's'} (encoded as {effectiveSigs.length} Allowed Calls element
                    {effectiveSigs.length === 1 ? '' : 's'}).
                  </p>
                )}

                {effectiveSigs.length > 0 && (
                  <ul className="mt-1 text-xs font-mono text-green-600/90 dark:text-green-400/90 space-y-0.5">
                    {effectiveSigs.map(sig => {
                      const sel = computeSelector(sig)
                      return (
                        <li key={allowedCallSignatureKey(sig)}>
                          {sel ? `→ ${sel}` : <span className="text-red-400">Invalid: {sig}</span>}
                        </li>
                      )
                    })}
                  </ul>
                )}

                {!entry.useAnyFunction && effectiveSigs.length === 0 && (
                  <p className="mt-2 text-xs text-amber-600/90 dark:text-amber-400/90">
                    Select at least one function, or enable &quot;Any function&quot;.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
